import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { SupportRegionId } from '@modules/internationalisation/countryGroup';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useRouteLoaderData, } from 'react-router';
import { ClientSideErrorPage } from 'components/ClientSideError';
import { GuardianHoldingContent } from 'components/serverSideRendered/guardianHoldingContent';
import { ObserverHoldingContent } from 'components/serverSideRendered/observerHoldingContent';
import { AbParticipationsProvider } from 'contexts/AbParticipationsContext';
import { FeatureSwitchesProvider } from 'contexts/FeatureSwitchesContext';
import { getStudentLandingPageTestConfig } from 'helpers/abTests/studentLandingPageAbTests';
import { WithCoreWebVitals } from 'helpers/coreWebVitals/withCoreWebVitals';
import { isSwitchOn } from 'helpers/globalsAndSwitches/globals';
import { isObserverSubdomain } from 'helpers/globalsAndSwitches/observer';
import { parseAppConfig } from 'helpers/globalsAndSwitches/window';
import { Country } from 'helpers/internationalisation/classes/country';
import { getAbParticipations, setUpConsent, setUpTracking, } from 'helpers/page/page';
import { renderPage } from 'helpers/rendering/render';
import { useStudentBeansRegionValid } from 'pages/[countryGroupId]/helpers/useStudentBeansRegionValid';
import { getCheckoutNudgeParticipations } from '../../helpers/abTests/checkoutNudgeAbTests';
import { getLandingPageParticipations } from '../../helpers/abTests/landingPageAbTests';
import { getOneTimeCheckoutParticipations } from '../../helpers/abTests/oneTimeCheckoutAbTests';
import { getPageParticipations, } from '../../helpers/abTests/pageParticipations';
const checkoutNudgeSettings = getCheckoutNudgeParticipations();
const appConfig = parseAppConfig(window.guardian);
async function rootLoader() {
    void setUpConsent();
    const [landing, oneTime, studentLanding] = await Promise.all([
        getLandingPageParticipations(),
        getOneTimeCheckoutParticipations(),
        getPageParticipations(getStudentLandingPageTestConfig()),
    ]);
    const finalParticipations = {
        ...getAbParticipations(),
        ...landing.participations,
        ...checkoutNudgeSettings?.participations,
        ...oneTime.participations,
        ...studentLanding.participations,
    };
    // Setup tracking (non-blocking)
    setUpTracking(finalParticipations);
    return { landing, oneTime, studentLanding, finalParticipations };
}
function useRootLoaderData() {
    return useRouteLoaderData('root');
}
function RootLayout() {
    const { finalParticipations } = useRootLoaderData();
    return (_jsx(WithCoreWebVitals, { children: _jsx(AbParticipationsProvider, { participations: finalParticipations, children: _jsx(Outlet, {}) }) }));
}
const router = createBrowserRouter([
    {
        id: 'root',
        loader: rootLoader,
        element: _jsx(RootLayout, {}),
        errorElement: _jsx(ClientSideErrorPage, {}),
        HydrateFallback: GuardianOrObserverHoldingContent,
        children: [
            ...Object.values(SupportRegionId).flatMap((supportRegionId) => [
                {
                    path: `/${supportRegionId}/contribute/:campaignCode?`,
                    lazy: async () => {
                        const { LandingPage } = await import(
                        /* webpackChunkName: "LandingPage" */ './landingPage');
                        return {
                            Component: function LandingPageRoute() {
                                const { landing: landingPageSettings } = useRootLoaderData();
                                return (_jsx(LandingPage, { supportRegionId: supportRegionId, landingPageSettings: landingPageSettings }));
                            },
                        };
                    },
                },
                {
                    path: `/${supportRegionId}/checkout`,
                    lazy: async () => {
                        const { Checkout } = await import(
                        /* webpackChunkName: "checkout" */ './checkout');
                        return {
                            Component: function CheckoutRoute() {
                                const { landing } = useRootLoaderData();
                                return (_jsx(Checkout, { supportRegionId: supportRegionId, appConfig: appConfig, landingPageSettings: landing.variant, nudgeSettings: checkoutNudgeSettings }));
                            },
                        };
                    },
                },
                {
                    path: `/${supportRegionId}/one-time-checkout`,
                    lazy: async () => {
                        const { OneTimeCheckout } = await import(
                        /* webpackChunkName: "oneTimeCheckout" */ './oneTimeCheckout');
                        return {
                            Component: function OneTimeCheckoutRoute() {
                                const { landing, oneTime } = useRootLoaderData();
                                return (_jsx(OneTimeCheckout, { supportRegionId: supportRegionId, appConfig: appConfig, nudgeSettings: checkoutNudgeSettings, landingPageSettings: landing.variant, oneTimeCheckoutSettings: oneTime.variant, userAttributes: oneTime.userAttributes ?? {} }));
                            },
                        };
                    },
                },
                {
                    path: `/${supportRegionId}/thank-you`,
                    lazy: async () => {
                        const { ThankYou } = await import(
                        /* webpackChunkName: "ThankYou" */ './thankYou');
                        return {
                            Component: function ThankYouRoute() {
                                const { landing } = useRootLoaderData();
                                return (_jsx(ThankYou, { supportRegionId: supportRegionId, appConfig: appConfig, landingPageSettings: landing.variant }));
                            },
                        };
                    },
                },
                {
                    path: `/${supportRegionId}/invitation/accept/:code`,
                    lazy: async () => {
                        const { Invitation } = await import(
                        /* webpackChunkName: "Invitation" */ './invitation');
                        return {
                            Component: function InvitationAcceptRoute() {
                                const { landing } = useRootLoaderData();
                                return (_jsx(Invitation, { supportRegionId: supportRegionId, landingPageSettings: landing.variant, mode: "accept" }));
                            },
                        };
                    },
                },
                {
                    path: `/${supportRegionId}/invitation/reject/:code`,
                    lazy: async () => {
                        const { Invitation } = await import(
                        /* webpackChunkName: "Invitation" */ './invitation');
                        return {
                            Component: function InvitationRejectRoute() {
                                const { landing } = useRootLoaderData();
                                return (_jsx(Invitation, { supportRegionId: supportRegionId, landingPageSettings: landing.variant, mode: "reject" }));
                            },
                        };
                    },
                },
                {
                    path: `/${supportRegionId}/guardian-ad-lite`,
                    lazy: async () => {
                        const { GuardianAdLiteLanding } = await import(
                        /* webpackChunkName: "GuardianAdLiteLanding" */ './guardianAdLiteLanding/guardianAdLiteLanding');
                        return {
                            Component: function GuardianAdLiteRoute() {
                                return (_jsx(GuardianAdLiteLanding, { supportRegionId: supportRegionId }));
                            },
                        };
                    },
                },
                {
                    path: `/${supportRegionId}/student`,
                    lazy: async () => {
                        const { StudentLandingPageGlobalContainer } = await import(
                        /* webpackChunkName: "StudentLandingPageGlobalContainer" */ './student/StudentLandingPageGlobalContainer');
                        return {
                            Component: function StudentRoute() {
                                const { landing } = useRootLoaderData();
                                const isStudentBeansRegionValid = useStudentBeansRegionValid(supportRegionId, Country.detect());
                                if (!isStudentBeansRegionValid) {
                                    return (_jsx(Navigate, { to: `/${supportRegionId}/contribute`, replace: true }));
                                }
                                return (_jsx(StudentLandingPageGlobalContainer, { supportRegionId: supportRegionId, landingPageVariant: landing.variant }));
                            },
                        };
                    },
                },
                {
                    /* NOTE: the back end routing filters out invalid paths based on the RRCP tooling config */
                    path: `/${supportRegionId}/student/:institution`,
                    lazy: async () => {
                        const { StudentLandingPageInstitutionContainer } = await import(
                        /* webpackChunkName: "StudentLandingPageInstitutionContainer" */ './student/StudentLandingPageInstitutionContainer');
                        return {
                            Component: function StudentRoute() {
                                const { landing, studentLanding } = useRootLoaderData();
                                const isSwitchedOn = isSwitchOn('featureSwitches.enableTooledStudentLandingPage');
                                if (!studentLanding.variant || !isSwitchedOn) {
                                    return null;
                                }
                                return (_jsx(StudentLandingPageInstitutionContainer, { supportRegionId: supportRegionId, landingPageVariant: landing.variant, studentLandingPageVariant: studentLanding.variant }));
                            },
                        };
                    },
                },
            ]),
        ],
    },
]);
function GuardianOrObserverHoldingContent() {
    if (isObserverSubdomain()) {
        return _jsx(ObserverHoldingContent, {});
    }
    return _jsx(GuardianHoldingContent, {});
}
function Router() {
    return (_jsx(FeatureSwitchesProvider, { children: _jsx(RouterProvider, { router: router }) }));
}
export default renderPage(_jsx(Router, {}));
